import React, { Component, useEffect, useState } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Inventory from './pages/Inventory'
import StorageBins from './pages/StorageBins'
import Sharing from './pages/Sharing'
import MeasurementSheet from './pages/MeasurementSheet'
import InviteAccept from './pages/InviteAccept'
import SignIn from './pages/auth/SignIn'
import SignUp from './pages/auth/SignUp'
import ForgotPassword from './pages/auth/ForgotPassword'
import ResetPassword from './pages/auth/ResetPassword'
import ChangePassword from './pages/auth/ChangePassword'
import AuthCallback from './pages/auth/AuthCallback'
import CookieCutterDetail from './pages/CookieCutterDetail'
import CookieCutterEdit from './pages/CookieCutterEdit'
import BulkProcessing from './pages/BulkProcessing'
import ConfigError from './components/ConfigError'
import { configError } from './lib/supabase'

class ErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md px-4">
            <div className="text-5xl mb-4">😵</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="text-left text-xs bg-red-50 text-red-700 p-3 rounded mb-4 overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, clearAuthCache } = useAuth()
  const [showRetryButton, setShowRetryButton] = useState(false)

  useEffect(() => {
    // Show retry button after 10 seconds if still loading
    const timer = setTimeout(() => {
      if (loading) {
        setShowRetryButton(true)
      }
    }, 10000)

    return () => clearTimeout(timer)
  }, [loading])

  const handleRetry = () => {
    // Use the improved auth cache clearing
    clearAuthCache()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-900 mb-4">Loading...</div>
          {showRetryButton && (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">Taking longer than expected?</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Clear Cache & Retry
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/signin" replace />
  }

  return <Layout>{children}</Layout>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, clearAuthCache } = useAuth()
  const [showRetryButton, setShowRetryButton] = useState(false)

  useEffect(() => {
    // Show retry button after 10 seconds if still loading
    const timer = setTimeout(() => {
      if (loading) {
        setShowRetryButton(true)
      }
    }, 10000)

    return () => clearTimeout(timer)
  }, [loading])

  const handleRetry = () => {
    // Use the improved auth cache clearing
    clearAuthCache()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-900 mb-4">Loading...</div>
          {showRetryButton && (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">Taking longer than expected?</p>
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Clear Cache & Retry
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  // Check for configuration errors
  if (configError) {
    return <ConfigError error={configError} />
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
            {/* Public routes */}
            <Route
              path="/auth/signin"
              element={
                <PublicRoute>
                  <SignIn />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/signup"
              element={
                <PublicRoute>
                  <SignUp />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/callback"
              element={<AuthCallback />}
            />
            
            {/* Protected routes */}
            <Route
              path="/auth/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/:id"
              element={
                <ProtectedRoute>
                  <CookieCutterDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory/edit/:id"
              element={
                <ProtectedRoute>
                  <CookieCutterEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/storage-bins"
              element={
                <ProtectedRoute>
                  <StorageBins />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sharing"
              element={
                <ProtectedRoute>
                  <Sharing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/measurement-sheet"
              element={
                <ProtectedRoute>
                  <MeasurementSheet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-processing"
              element={
                <ProtectedRoute>
                  <BulkProcessing />
                </ProtectedRoute>
              }
            />
            
            {/* Special invitation acceptance route - can be accessed without login */}
            <Route
              path="/invite/:token"
              element={<InviteAccept />}
            />
            
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
